import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";

import { confirmSignedErrorMessage } from "./contractErrors";

interface ConfirmSignedDialogProps {
  visible: boolean;
  onHide: () => void;
  // Returns true on success (dialog closes); throws on server error (mapped inline).
  onSubmit: (signedContractUrl: string) => Promise<boolean>;
}

// Lightweight client-side check; the server is authoritative on the exact shape
// it accepts. Deliberately generic: pinning this to one storage provider is what
// broke the dialog when contracts moved off Google Drive (CWA-118).
const looksLikeDocumentLink = (url: string): boolean => /^https?:\/\/.+/i.test(url);

/**
 * US2 (digital track): confirm the digital signature by pasting the link to the
 * stored signed document. Rejects an empty/unrecognised link client-side
 * (FR-007) and maps server rejections to an inline message while staying open.
 */
const ConfirmSignedDialog: React.FC<ConfirmSignedDialogProps> = ({
  visible,
  onHide,
  onSubmit,
}) => {
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) {
      setUrl("");
      setError(null);
      setSubmitting(false);
    }
  }, [visible]);

  const submit = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError("Please paste the signed document's link.");
      return;
    }
    if (!looksLikeDocumentLink(trimmed)) {
      setError("That doesn't look like a document link.");
      return;
    }
    setSubmitting(true);
    try {
      const ok = await onSubmit(trimmed);
      if (ok) onHide();
    } catch (e) {
      setError(confirmSignedErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button
        label="Confirm signed"
        icon="pi pi-check"
        onClick={submit}
        loading={submitting}
        disabled={submitting}
      />
    </div>
  );

  return (
    <Dialog
      header="Confirm signed contract"
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "36rem" }}
    >
      {error && <Message severity="error" className="w-full mb-3" text={error} />}
      <p className="text-500 mt-0">
        Paste the link to the stored signed document. You can copy it from the
        contract details above.
      </p>
      <span className="p-fluid">
        <InputText
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError(null);
          }}
          placeholder="Link to the signed document"
          aria-label="Signed document link"
        />
      </span>
    </Dialog>
  );
};

export default ConfirmSignedDialog;
